/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import MediaPrepHeaderSection from "../../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../../common/commonComponents/mediaPrepPageHeader";
import MDHeader from "./partials/MDHeader";
import MDMediaBatchDetail from "./partials/MDMediaBatchDetail";
import { useEffect, useState } from "react";
import { MediaBatchData } from "../../types/mediaBatchCommon.types";
import { MediaBatchPreparationInitServices } from "../../../../../service/mediaBatchPrepInitServices";
import {
	CapacityMapping,
	Container,
	ContainerBatch,
	ContainerLot,
	DispenseEntryPayload,
	DispenseState,
} from "../../types/mediaBatchDispense.types";
import getDispenseColumns from "./partials/columns";
import BatchLotInfoCards from "./partials/BatchInfoCards";
import ContainerTable from "./partials/ContainerTable";
import ContainerSearch from "./partials/ContainerSearch";
import ContainerSelectionForm from "./partials/ContainerSelectionForm";
import DispensingHeader from "./partials/DispenseHeader";
import DispensingControlPanel from "./partials/DispenseControlPanel";
import DispenseHistoryTable from "./partials/DispenseHistoryTable";
import DispenseEntryFooter from "./partials/DispenseEntryFooter";
import CustomToast from "../../../../../widgets/toaster";
import { mediaPreparationService } from "../../../../../service/mediaPrepBatchServices";

interface Props {
	onNext: () => void;
	onBack: () => void;
	batchId: number;
}

const MediaDispenseEntry = ({ onNext, onBack, batchId }: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [, setSearchParams] = useSearchParams();
	const [errors, setErrors] = useState({
		containerDimension: "",
		containerBatch: "",
		containerLot: "",
		containerSelection: "",
	});
	const [mediaBatchData, setMediaBatchData] = useState<MediaBatchData | null>(
		null,
	);

	const [searchQuery, setSearchQuery] = useState("");
	const [containerLots, setContainerLots] = useState<ContainerLot[]>([]);
	const [containerBatches, setContainerBatches] = useState<ContainerBatch[]>(
		[],
	);
	const [containerDimensions, setContainerDimensions] = useState<
		CapacityMapping[]
	>([]);
	const [containers, setContainers] = useState<Container[]>([]);

	const [selectedContainerBatch, setSelectedContainerBatch] =
		useState<ContainerBatch | null>(null);
	const [selectedContainerLot, setSelectedContainerLot] =
		useState<ContainerLot | null>(null);
	const [selectedContainerDimension, setSelectedContainerDimension] =
		useState<CapacityMapping | null>(null);

	const [dispenseData, setDispenseData] = useState<DispenseState>({});
	const [nominalValue, setNominalValue] = useState<number>(0);

	const [totalYieldUnits, setTotalYieldUnits] = useState<number>(0);

	const [solutionRemainingQty, setSolutionRemainingQty] = useState<number>(0);
	const [refreshTrigger, setRefreshTrigger] = useState<boolean>(false);

	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [batchList, setBatchList] = useState<any[]>([]);
	const [selectedBatchDetails, setSelectedBatchDetails] = useState<ContainerBatch | null>(null);

	const getBatchList = async () => {
		try {
			const response =
				await mediaPreparationService.mediaPrepgetBatchCodes();

			if (response.status) {
				setBatchList(response.data);
			}
		} catch (err) {
			console.error(err);
		}
	};
	const [batchKeyword, setBatchKeyword] = useState("");
	const handleBatchChange = (batchId: string) => {
		const selectedBatch = batchList.find(
			(batch) => String(batch.id) === String(batchId)
		);
		if (!selectedBatch) return;

		// Don't navigate to Dispense Entry for completed batches
		if (
			selectedBatch.status === "Ready to Use" ||
			selectedBatch.status === "Rejected"
		) {
			return;
		}
		setSearchParams({ batchId });
	};

	const [toast, setToast] = useState({
		show: false,
		message: "",
		toastType: "error", // or "error"
	});

	const showToast = (message: string, toastType: "success" | "error") => {
		setToast({ show: true, message, toastType });
	};

	const goBack = () => {
		onBack();
	};

	const getViewPreparationDetails = async () => {
		try {
			const response =
				await MediaBatchPreparationInitServices.getViewPreparationDetails({
					id: batchId,
				});
			setMediaBatchData(response.data);
			setSolutionRemainingQty(
				Number(response.data.solution_consumption[0].quantity_used),
			);
		} catch (error) {
			console.error(error);
		}
	};

	const getContainerDimensions = async () => {
		if (!mediaBatchData?.default_container?.container_type_id) return;
		const urlParams = {
			page: 1,
			size: 50,
			sort: "id",
			order: "desc",
			container_type_id:
				mediaBatchData?.default_container?.container_type_id,
		};
		try {
			const response =
				await MediaBatchPreparationInitServices.getContainerDimensions(
					urlParams,
				);
			setContainerDimensions(response.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getContainerBatches = async () => {
		if (
			!mediaBatchData?.default_container?.container_type_id ||
			!selectedContainerDimension
		)
			return;
		const urlParams = {
			page: 1,
			size: 50,
			sort: "id",
			order: "desc",
			container_type_id: mediaBatchData.default_container.container_type_id,
			container_size_id: selectedContainerDimension?.id,
		};
		try {
			const response =
				await MediaBatchPreparationInitServices.getContainerBatches(
					urlParams,
				);
			setContainerBatches(response.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getSelectedContainerBatchDetails = async (batchNo: string) => {
		if (
			!mediaBatchData?.default_container?.container_type_id ||
			!selectedContainerDimension
		)
			return;

		const urlParams = {
			page: 1,
			size: 1,
			sort: "id",
			order: "desc",
			// container_type_id: mediaBatchData.default_container.container_type_id,
			// container_size_id: selectedContainerDimension.id,
			batch_no: batchNo,
		};

		try {
			const response =
				await MediaBatchPreparationInitServices.getContainerBatches(
					urlParams
				);

			setSelectedBatchDetails(response.data?.[0] ?? null);
		} catch (error) {
			console.error(error);
		}
	};

	const getContainerLots = async () => {
		if (!selectedContainerBatch) return;
		const urlParams = {
			page: 1,
			size: 50,
			sort: "id",
			order: "desc",
			container_batch_id: selectedContainerBatch.id,
		};
		try {
			const response =
				await MediaBatchPreparationInitServices.getContainerLots(urlParams);
			setContainerLots(response.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getContainers = async () => {
		if (!selectedContainerBatch || !selectedContainerLot) return;
		const urlParams = {
			page: page,
			size: 50,
			order: "desc",
			container_batch_id: selectedContainerBatch.id,
			lot_id: selectedContainerLot.id,
			keyword: searchQuery,
		};
		try {
			const response =
				await MediaBatchPreparationInitServices.getContainers(urlParams);
			const emptyContainers = response.data.filter(
				(c) => c.operational_status === "EMPTY_AVAILABLE",
			);
			setContainers(emptyContainers);
			setTotalPages(response.totalPages);
			setPage(Number(response.page));
		} catch (error) {
			console.error(error);
		}
	};

	const handleCheck = (id: string, checked: boolean) => {
		setDispenseData((prev) => ({
			...prev,
			[id]: {
				...prev[id],
				checked,
				actual: prev[id]?.actual ?? nominalValue,
			},
		}));

		if (checked) {
			setErrors(prev => ({
				...prev,
				containerSelection: "",
			}));
		}
	};

	const handleActualChange = (id: string, actual: number) => {
		setDispenseData((prev) => ({
			...prev,
			[id]: {
				...prev[id],
				actual,
				checked: prev[id]?.checked ?? false,
			},
		}));
	};

	const handleContainerBatchChange = (batch: ContainerBatch) => {
		setSelectedContainerBatch(batch);

		setErrors(prev => ({
			...prev,
			containerBatch: "",
		}));
		getSelectedContainerBatchDetails(batch.batch_no);
	};

	const handleContainerLotChange = (lot: ContainerLot) => {
		setSelectedContainerLot(lot);

		setErrors(prev => ({
			...prev,
			containerLot: "",
		}));
	};

	const handleContainerDimensionChange = (id: string) => {
		const newDimension = containerDimensions.find((c) => c.id === id)!;
		setSelectedContainerDimension(newDimension);
		setErrors(prev => ({
			...prev,
			containerDimension: "",
		}));

		const newNominalValue = Number(newDimension?.nominal_value) || 0;
		setNominalValue(newNominalValue);

		const newTotalYield =
			newNominalValue > 0
				? Math.ceil(Number(solutionRemainingQty) / newNominalValue)
				: 0;

		setTotalYieldUnits(newTotalYield);
	};

	const isValidPayload = (payload: DispenseEntryPayload): boolean => {
		const invalidEntries = Object.entries(payload).filter(
			([_, value]) =>
				Number.isNaN(value) || value === null || value === undefined,
		);

		if (invalidEntries.length > 0) {
			console.error(
				"Payload validation failed on keys:",
				invalidEntries.map(([k]) => k),
			);
			return false;
		}
		return true;
	};

	const onDispense = async (status: string): Promise<any> => {
		const selectedIds = Object.keys(dispenseData).filter(
			(key) => dispenseData[key].checked,
		);
		const newErrors = {
			containerDimension: "",
			containerBatch: "",
			containerLot: "",
			containerSelection: "",
		};
		let hasError = false;

		// These validations apply to both Dispense and Complete
		if (!selectedContainerDimension) {
			newErrors.containerDimension = "Please select Container Dimension";
			hasError = true;
		}

		if (!selectedContainerBatch) {
			newErrors.containerBatch = "Please select Container Batch";
			hasError = true;
		}

		if (!selectedContainerLot) {
			newErrors.containerLot = "Please select Container Lot";
			hasError = true;
		}

		// This validation applies ONLY to Complete
		const requiredContainers = validRemainingUnits ?? totalYieldUnits;
		if (
			status === "Completed" &&
			selectedIds.length !== requiredContainers
		) {
			newErrors.containerSelection = `Please select ${requiredContainers} container(s).`;
			hasError = true;
		}

		setErrors(newErrors);
		if (hasError) return false;

		try {
			const payload: DispenseEntryPayload = {
				media_batch_id: Number(mediaBatchData?.id),
				container_capacity_mapping_id: Number(
					selectedContainerDimension?.id,
				),
				container_type_id: Number(
					mediaBatchData?.default_container?.container_type_id,
				),
				container_batch_id: Number(selectedContainerBatch?.id),
				container_lot_id: Number(selectedContainerLot?.id),
				units_dispensed: selectedContainerCount,
				dispensed_container_ids: selectedIds,
				target_yield_units: totalYieldUnits,
				discarded_units: 0,
				wastage_reason_id: 0,
				discard_remarks: "",
				remarks: "",
				dispensing_status: status,
			};
			if (!isValidPayload(payload)) return false;
			const response = await MediaBatchPreparationInitServices.postDispenseEntry(payload);
			return response;
		} catch (error: any) {
			showToast(
				error?.response?.data?.message || "An unexpected error occurred",
				"error",
			);
			console.error(error);
			return false;
		}
	};

	const clearDispenseData = () => {
		setDispenseData({});
	};

	const getSelectedContainerCount = (): number => {
		return Object.values(dispenseData).filter((d) => d.checked).length;
	};

	const availableVolume = solutionRemainingQty;
	const selectedContainerCount = getSelectedContainerCount();
	const usedVolume = mediaBatchData?.dispensing_summary
		? Number(mediaBatchData.dispensing_summary.total_dispensed_volume_ml)
		: Math.min(
				availableVolume,
				nominalValue * selectedContainerCount,
		);

	const remainingVolume = mediaBatchData?.dispensing_summary
		? Number(mediaBatchData.dispensing_summary.remaining_volume_ml)
		: Math.max(
				0,
				availableVolume - usedVolume,
		);

	const alreadyDispensed =
		mediaBatchData?.dispensing_summary?.cycles?.reduce(
			(sum, cycle) => sum + Number(cycle.units_dispensed ?? 0),
			0,
		) ?? 0;

	// const isLimitReached: boolean =
	// 	totalYieldUnits - (alreadyDispensed + getSelectedContainerCount()) <= 0;
	const isLimitReached =
	 solutionRemainingQty <= 0 || (totalYieldUnits > 0 && getSelectedContainerCount() >= totalYieldUnits);


	const rawUnits = mediaBatchData?.dispensing_summary?.remaining_units;
	// Check if it's not null/undefined AND is a valid number once converted
	const validRemainingUnits =
		rawUnits != null && !isNaN(Number(rawUnits))
			? Number(rawUnits)
			: undefined;

	const requiredContainerCount =
	nominalValue > 0
		? Math.ceil(solutionRemainingQty / nominalValue)
		: 0;		

	const columns = getDispenseColumns(
		t,
		nominalValue,
		dispenseData,
		handleCheck,
		handleActualChange,
		isLimitReached,
	);

	useEffect(() => {
		// this one will call the getContainertypes and types will be filled.
		getViewPreparationDetails();
	}, [batchId]);

	useEffect(() => {
		getContainerDimensions();
	}, [mediaBatchData?.default_container]);

	// useEffect(() => {
	// 	setSelectedContainerDimension(null);
	// 	setSelectedContainerBatch(null);
	// 	setSelectedContainerLot(null);
	// 	setContainers([]);
	// 	clearDispenseData();
	// 	getContainerDimensions();
	// }, [selectedContainerType]);

	useEffect(() => {
		setSelectedContainerBatch(null);
		setSelectedContainerLot(null);
		getContainerBatches();
		clearDispenseData();
		setContainers([]);
	}, [selectedContainerDimension]);

	useEffect(() => {
		setSelectedContainerLot(null);
		getContainerLots();
		clearDispenseData();
		setContainers([]);
	}, [selectedContainerBatch, refreshTrigger]);

	useEffect(() => {
		getContainers();
		clearDispenseData();
	}, [selectedContainerLot, searchQuery, page]);

	useEffect(() => {
		if (!batchKeyword) {
			getBatchList();
			return;
		}
		const timer = setTimeout(() => {
			getBatchList();
		}, 300);
		return () => clearTimeout(timer);
	}, [batchId, batchKeyword]);


	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("mediaBatchInit.title")} />
			<div className="flex flex-col min-h-screen bg-[#F1F5F9] font-['Inter'] text-[#1E293B] pb-1">
				<MDHeader onBack={goBack} />
				<div className="p-8 space-y-8 max-w-[1920px] mx-auto w-full">
					<MDMediaBatchDetail 
						mediaBatchData={mediaBatchData}
						availableVolume={availableVolume}
						usedVolume={usedVolume}
						remainingVolume={remainingVolume}
						batchList={batchList}
						onBatchChange={handleBatchChange}
						onSearch={setBatchKeyword}
					/>

					<div className="bg-white rounded-[1rem] shadow-2xl shadow-slate-200/50 border border-slate-200 overflow-hidden flex flex-col">
						<DispensingHeader />
						<div className="p-10">
							<div className="grid grid-cols-12 gap-10">
								<div className="col-span-8 space-y-10">
									<ContainerSelectionForm
										defaultContainer={mediaBatchData?.default_container}
										containerBatches={containerBatches}
										containerLots={containerLots}
										containerDimensions={containerDimensions}
										selectedContainerDimension={selectedContainerDimension}
										selectedContainerBatch={selectedContainerBatch}
										selectedContainerLot={selectedContainerLot}
										setSelectedContainerBatch={handleContainerBatchChange}
										setSelectedContainerLot={handleContainerLotChange}
										handleContainerDimensionChange={handleContainerDimensionChange}
										errors={errors}
									/>
									<ContainerSearch
										searchQuery={searchQuery}
										setSearchQuery={setSearchQuery}
									/>
									<BatchLotInfoCards selectedContainerBatch={selectedBatchDetails} />
									<ContainerTable
										page={page}
										setPage={setPage}
										totalPages={totalPages}
										containers={containers}
										columns={columns}
										containerError={errors.containerSelection}
										t={t}
									/>
								</div>
								{/* DISPENSING CONTROL PANEL (Right side of center focus) */}

								<div className="col-span-4 space-y-8">
									<DispensingControlPanel
										handlePartialDispense={() =>
											setRefreshTrigger((p) => !p)
										}
										alreadyDispensed={alreadyDispensed}
										selectedContainerCount={getSelectedContainerCount()}
										nominalValue={nominalValue}
										totalYieldUnits={totalYieldUnits}
										onDispense={onDispense}
										onClear={() => setDispenseData({})}
										getMediaBatchData={getViewPreparationDetails}
										requiredContainerCount={
											validRemainingUnits ?? totalYieldUnits
										}
									/>
								</div>
							</div>
						</div>
					</div>
					{/* <DispenseHistoryTable /> */}
				</div>
				<DispenseEntryFooter
					onDispense={onDispense}
					isLimitReached={isLimitReached}
					dispenseStatus={
						mediaBatchData?.dispensing_summary?.dispensing_status ??
						"pending"
					}
					onNext={onNext}
					showToast={showToast}
				/>
			</div>
			<CustomToast
				show={toast.show}
				message={toast.message}
				toastType={toast.toastType}
				delay={1200}
				onClose={() => setToast((prev) => ({ ...prev, show: false }))}
				toastPosition="topRight" // optional
			/>
		</div>
	);
};

export default MediaDispenseEntry;
